// Distance Utilities - Calculate distances and format for display

export interface Coordinates {
  latitude: number;
  longitude: number;
}

/**
 * Calculate distance between two coordinates using Haversine formula
 * Returns distance in kilometers
 */
export function calculateDistance(
  point1: Coordinates,
  point2: Coordinates
): number {
  const R = 6371; // Earth's radius in km

  const dLat = toRadians(point2.latitude - point1.latitude);
  const dLon = toRadians(point2.longitude - point1.longitude);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(point1.latitude)) *
      Math.cos(toRadians(point2.latitude)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Format distance for display
 * Can accept either a distance in km or two coordinates
 */
export function formatDistance(
  distanceOrPoint1: number | Coordinates,
  point2?: Coordinates
): string {
  let distanceKm: number;

  if (typeof distanceOrPoint1 === 'number') {
    distanceKm = distanceOrPoint1;
  } else if (point2) {
    distanceKm = calculateDistance(distanceOrPoint1, point2);
  } else {
    return '';
  }

  if (distanceKm < 0.1) {
    return 'Direkt hier';
  }

  if (distanceKm < 1) {
    const meters = Math.round(distanceKm * 1000);
    return `${meters} m`;
  }

  if (distanceKm < 10) {
    return `${distanceKm.toFixed(1)} km`;
  }

  return `${Math.round(distanceKm)} km`;
}

/**
 * Estimate walking time (average 5 km/h)
 */
export function estimateWalkingTime(distanceKm: number): string {
  const minutes = Math.round((distanceKm / 5) * 60);

  if (minutes < 1) {
    return '< 1 Min';
  }

  if (minutes < 60) {
    return `${minutes} Min zu Fuss`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes === 0) {
    return `${hours} Std zu Fuss`;
  }

  return `${hours}:${remainingMinutes.toString().padStart(2, '0')} Std`;
}

/**
 * Get distance badge text (combines distance and walking time)
 */
export function getDistanceBadge(
  userLocation: Coordinates | null,
  placeLocation: Coordinates | null
): {
  distance: string;
  walkingTime: string;
  distanceKm: number;
} | null {
  if (!userLocation || !placeLocation) {
    return null;
  }

  const distanceKm = calculateDistance(userLocation, placeLocation);

  return {
    distance: formatDistance(distanceKm),
    walkingTime: estimateWalkingTime(distanceKm),
    distanceKm,
  };
}

/**
 * Sort places by distance from user
 */
export function sortByDistance<T extends { coordinates?: Coordinates | null }>(
  places: T[],
  userLocation: Coordinates
): T[] {
  return [...places].sort((a, b) => {
    if (!a.coordinates && !b.coordinates) return 0;
    if (!a.coordinates) return 1;
    if (!b.coordinates) return -1;

    const distA = calculateDistance(userLocation, a.coordinates);
    const distB = calculateDistance(userLocation, b.coordinates);

    return distA - distB;
  });
}

/**
 * Filter places by radius from a center point
 */
export function filterPlacesByRadius<T extends { latitude?: number; longitude?: number }>(
  places: T[],
  center: Coordinates,
  radiusKm: number
): T[] {
  return places.filter((place) => {
    if (!place.latitude || !place.longitude) return false;

    const distance = calculateDistance(center, {
      latitude: place.latitude,
      longitude: place.longitude,
    });

    return distance <= radiusKm;
  });
}

/**
 * Sort places by distance from a center point (using latitude/longitude directly)
 */
export function sortPlacesByDistance<T extends { latitude?: number; longitude?: number }>(
  places: T[],
  center: Coordinates
): T[] {
  return [...places].sort((a, b) => {
    if (!a.latitude || !a.longitude) return 1;
    if (!b.latitude || !b.longitude) return -1;

    const distA = calculateDistance(center, { latitude: a.latitude, longitude: a.longitude });
    const distB = calculateDistance(center, { latitude: b.latitude, longitude: b.longitude });

    return distA - distB;
  });
}

/**
 * Add distance to places
 */
export function addDistanceToPlaces<T extends { latitude?: number; longitude?: number }>(
  places: T[],
  center: Coordinates
): (T & { distance?: number })[] {
  return places.map((place) => {
    if (!place.latitude || !place.longitude) {
      return { ...place, distance: undefined };
    }

    const distance = calculateDistance(center, {
      latitude: place.latitude,
      longitude: place.longitude,
    });

    return { ...place, distance };
  });
}
